/**
=========================================================
* Trust Cargo - public site header
=========================================================

* Transparent over the hero, then solid navy once you scroll past it - so the
* opening image is never cut in half by a bar, but the links stay reachable
* all the way down the page.
*
* The login button is the door to the internal system: it reads the session
* and says "Dashboard" to someone already signed in.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Drawer from "@mui/material/Drawer";
import { useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { systemHome } from "components/SystemHome";
import { C } from "./ui";

/** The Trust Cargo mark: a gold container badge, drawn not downloaded. */
function Logo({ small }) {
  const size = small ? 38 : 46;

  return (
    <SuiBox
      sx={{
        width: size,
        height: size,
        borderRadius: 2.5,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
        boxShadow: "0 8px 20px rgba(201,162,39,0.35)",
      }}
    >
      <Icon sx={{ color: C.navy, fontSize: `${small ? 22 : 26}px !important` }}>
        directions_boat
      </Icon>
    </SuiBox>
  );
}

Logo.defaultProps = { small: false };
Logo.propTypes = { small: PropTypes.bool };

function Nav({ t, lang, onLang, sections }) {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // someone with a live session gets sent straight into the dashboard
  const signedIn = Boolean(sessionStorage.getItem("role_id"));

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const enterSystem = () => navigate(systemHome());

  const linkSx = {
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 600,
    color: C.ivory,
    padding: "8px 2px",
    position: "relative",
    whiteSpace: "nowrap",
    transition: "color 160ms ease",
    "&:after": {
      content: '""',
      position: "absolute",
      insetInline: 0,
      bottom: 2,
      height: 2,
      borderRadius: 2,
      background: C.gold,
      transform: "scaleX(0)",
      transformOrigin: "center",
      transition: "transform 200ms ease",
    },
    "&:hover": { color: C.goldLight },
    "&:hover:after": { transform: "scaleX(1)" },
  };

  return (
    <SuiBox
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        insetInline: 0,
        zIndex: 1200,
        transition: "background-color 260ms ease, box-shadow 260ms ease, padding 260ms ease",
        backgroundColor: solid ? "rgba(6, 15, 34, 0.94)" : "transparent",
        backdropFilter: solid ? "blur(10px)" : "none",
        boxShadow: solid ? "0 10px 30px rgba(0,0,0,0.35)" : "none",
        borderBottom: solid ? `1px solid ${C.line}` : "1px solid transparent",
      }}
    >
      <SuiBox
        sx={{
          maxWidth: 1220,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: solid ? 1.25 : 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: "padding 260ms ease",
        }}
      >
        <SuiBox
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
          onClick={() => go("top")}
        >
          <Logo small={solid} />
          <SuiBox>
            <SuiTypography sx={{ fontSize: 19, fontWeight: 800, color: C.white, lineHeight: 1.15 }}>
              {t.brand}
            </SuiTypography>
            <SuiTypography sx={{ fontSize: 11.5, color: C.gold, fontWeight: 600 }}>
              {t.brandSub}
            </SuiTypography>
          </SuiBox>
        </SuiBox>

        {/* the links collapse into a drawer below md */}
        <SuiBox
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            gap: 3,
            marginInlineStart: "auto",
          }}
        >
          {sections.map((s) => (
            <SuiTypography key={s.id} sx={linkSx} onClick={() => go(s.id)}>
              {s.label}
            </SuiTypography>
          ))}
        </SuiBox>

        <SuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            marginInlineStart: { xs: "auto", lg: 0 },
          }}
        >
          {/* AR / EN */}
          <SuiBox
            component="button"
            type="button"
            onClick={() => onLang(lang === "ar" ? "en" : "ar")}
            aria-label="Change language"
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 13,
              color: C.ivory,
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${C.line}`,
              borderRadius: 2,
              px: 1.5,
              py: 0.9,
              "&:hover": { borderColor: C.gold, color: C.goldLight },
            }}
          >
            {lang === "ar" ? "EN" : "عربي"}
          </SuiBox>

          <SuiBox
            component="button"
            type="button"
            onClick={enterSystem}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 14.5,
              color: C.navy,
              background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
              border: "none",
              borderRadius: 2.5,
              px: 2.5,
              py: 1.2,
              boxShadow: "0 8px 22px rgba(201,162,39,0.32)",
              transition: "transform 160ms ease",
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            <Icon sx={{ fontSize: "18px !important" }}>{signedIn ? "dashboard" : "lock_open"}</Icon>
            {signedIn ? t.nav.account : t.nav.login}
          </SuiBox>

          <SuiBox
            component="button"
            type="button"
            aria-label="Menu"
            onClick={() => setOpen(true)}
            sx={{
              display: { xs: "grid", lg: "none" },
              placeItems: "center",
              cursor: "pointer",
              width: 42,
              height: 42,
              color: C.ivory,
              background: "rgba(255,255,255,0.08)",
              border: `1px solid ${C.line}`,
              borderRadius: 2,
            }}
          >
            <Icon>menu</Icon>
          </SuiBox>
        </SuiBox>
      </SuiBox>

      <Drawer
        anchor={t.dir === "rtl" ? "right" : "left"}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 290, backgroundColor: C.navy, borderInlineEnd: "none" } }}
      >
        <SuiBox sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }} dir={t.dir}>
          <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Logo small />
            <SuiTypography sx={{ fontSize: 18, fontWeight: 800, color: C.white }}>
              {t.brand}
            </SuiTypography>
          </SuiBox>

          {sections.map((s) => (
            <SuiTypography
              key={s.id}
              onClick={() => go(s.id)}
              sx={{
                cursor: "pointer",
                color: C.ivory,
                fontWeight: 600,
                fontSize: 16,
                py: 1.25,
                borderBottom: `1px solid rgba(255,255,255,0.06)`,
                "&:hover": { color: C.goldLight },
              }}
            >
              {s.label}
            </SuiTypography>
          ))}

          <SuiBox
            component="button"
            type="button"
            onClick={enterSystem}
            sx={{
              mt: 2,
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 15,
              color: C.navy,
              background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
              border: "none",
              borderRadius: 2.5,
              px: 2.5,
              py: 1.4,
            }}
          >
            {signedIn ? t.nav.account : t.nav.login}
          </SuiBox>

          <SuiTypography sx={{ fontSize: 12, color: C.muted, mt: 1, lineHeight: 1.7 }}>
            {t.loginNote}
          </SuiTypography>
        </SuiBox>
      </Drawer>
    </SuiBox>
  );
}

Nav.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  t: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onLang: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }))
    .isRequired,
};

export default Nav;
